import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { getApiError, getCourse, updateCourseStatus } from "../../services/courseApi";
import "./course.css";

function Value({ label, children }) {
  return (
    <div className="course-value">
      <span>{label}</span>
      <strong>{children ?? "—"}</strong>
    </div>
  );
}

function formatMoney(payment) {
  if (!payment) return "—";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: payment.currency || "INR",
    maximumFractionDigits: 0,
  }).format(payment.amount || 0);
}

export default function CourseDetail() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  async function load() {
    setLoading(true);
    try {
      setCourse(await getCourse(courseId));
    } catch (error) {
      toast.error(getApiError(error, "Could not load course"));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId]);

  async function changeStatus() {
    const nextStatus = course.status === "PUBLISHED" ? "ARCHIVED" : "PUBLISHED";
    const verb = nextStatus === "PUBLISHED" ? "publish" : "archive";
    if (!window.confirm(`Are you sure you want to ${verb} this course?`)) return;
    setSaving(true);
    try {
      await updateCourseStatus(courseId, nextStatus);
      toast.success(`Course ${nextStatus === "PUBLISHED" ? "published" : "archived"}`);
      await load();
    } catch (error) {
      toast.error(getApiError(error, `Could not ${verb} course`));
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <div className="course-state"><div className="spinner-border text-primary" /></div>;
  }

  if (!course) {
    return (
      <div className="course-state">
        <h1>Course not found</h1>
        <button className="btn btn-primary" onClick={() => navigate("/courses")}>Back</button>
      </div>
    );
  }

  return (
    <main className="course-page">
      <Link className="course-back-link" to="/courses">
        <i className="bi bi-arrow-left me-2" />Back to courses
      </Link>

      <div className="course-page-header mt-3">
        <div>
          <div className="d-flex align-items-center gap-2 mb-2">
            <span className={`course-status ${course.status?.toLowerCase()}`}>
              {course.status}
            </span>
            <span className="text-muted small">Version {course.version}</span>
          </div>
          <h1>{course.title}</h1>
          <p className="text-muted mb-0">{course.short_description || "No short description"}</p>
        </div>
        <div className="d-flex gap-2 flex-wrap">
          <button
            className={`btn ${
              course.status === "PUBLISHED" ? "btn-outline-danger" : "btn-outline-success"
            }`}
            disabled={saving}
            onClick={changeStatus}
          >
            {course.status === "PUBLISHED" ? "Archive" : "Publish"}
          </button>
          <Link className="btn btn-primary" to={`/courses/${courseId}/edit`}>
            <i className="bi bi-pencil me-2" />Edit course
          </Link>
        </div>
      </div>

      <section className="course-summary-grid">
        <Value label="Music field">{course.music_field?.name}</Value>
        <Value label="Provider">{course.provider?.name}</Value>
        <Value label="Course type">{course.type === "ONE_OFF" ? "One-off" : "Graded"}</Value>
        <Value label="Delivery">{course.delivery_mode?.replaceAll("_", " ")}</Value>
        <Value label="Price">{formatMoney(course.payment)}</Value>
        <Value label="Validity">{course.validity_months} months</Value>
        <Value label="Sessions">{course.session_count}</Value>
        <Value label="Default duration">{course.session_duration_minutes} minutes</Value>
      </section>

      <section className="course-panel">
        <h2>About the course</h2>
        <p className="mb-0 course-description">{course.description || "No description provided."}</p>
      </section>

      <section className="course-panel">
        <div className="course-section-heading">
          <div>
            <h2>Curriculum</h2>
            <p className="text-muted mb-0">
              {course.topics?.length || 0} topics · {course.session_count} sessions
            </p>
          </div>
        </div>
        <div className="course-curriculum-readonly">
          {(course.topics || []).map((topic, topicIndex) => (
            <details open={topicIndex === 0} key={topic.topic_id || topicIndex}>
              <summary>
                <span className="course-order-number">{topicIndex + 1}</span>
                <span>
                  <strong>{topic.title}</strong>
                  <small>{topic.subtopics?.length || 0} subtopics</small>
                </span>
              </summary>
              <div className="course-readonly-body">
                {topic.description && <p className="text-muted">{topic.description}</p>}
                {(topic.subtopics || []).map((subtopic, subtopicIndex) => (
                  <div className="course-readonly-subtopic" key={subtopic.subtopic_id || subtopicIndex}>
                    <h3>{subtopicIndex + 1}. {subtopic.title}</h3>
                    {subtopic.description && <p className="text-muted">{subtopic.description}</p>}
                    <div className="course-session-list">
                      {(subtopic.sessions || []).map((session, sessionIndex) => (
                        <Link
                          className="course-session-row course-session-link"
                          key={session.curriculum_session_id || sessionIndex}
                          to={`/courses/${courseId}/sessions/${session.curriculum_session_id}/content`}
                        >
                          <span className="course-session-icon"><i className="bi bi-play-fill" /></span>
                          <div>
                            <strong>{session.title}</strong>
                            <small>
                              {session.duration_minutes} minutes
                            </small>
                          </div>
                          <span className="course-session-manage">Manage content <i className="bi bi-chevron-right" /></span>
                        </Link>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </details>
          ))}
        </div>
      </section>

      <section className="course-panel">
        <h2>Rules</h2>
        <div className="course-rules-grid">
          <Value label="Completion rule">{course.completion_rule?.replaceAll("_", " ")}</Value>
          <Value label="Assignment gate">{course.assignment_progress_gate?.replaceAll("_", " ")}</Value>
          {course.assignment_progress_gate === "MINIMUM_SCORE" && (
            <Value label="Minimum score">
              {course.assignment_minimum_score} / {course.assignment_max_score}
            </Value>
          )}
          <Value label="Cancellation limit">{course.cancellation_limit}</Value>
        </div>
      </section>
    </main>
  );
}
